import { MODULE_ALIASES } from 'src/permissions/module-registry';

export type PermissionActionKey =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'import'
  | 'export'
  | 'manage'
  | 'update'
  | 'publish'
  | 'execute'
  | 'pause'
  | 'resume'
  | 'cancel'
  | 'executions'
  | 'versions'
  | 'stats'
  | 'test'
  | 'metadata'
  | 'merge'
  | 'split'
  | 'channelflag'
  | 'privatenote'
  | 'privatenotetag';

export type PermissionsMap = Record<string, Partial<Record<PermissionActionKey, boolean>>>;

/** Backend / roles may use `bill`, `billing`, or mixed casing on the permissions map. */
export function hasBillingViewPermission(permissions: PermissionsMap | null): boolean {
  if (!permissions) return false;
  if (permissions.billing?.view || permissions.bill?.view) return true;
  return Object.entries(permissions).some(([key, actions]) => {
    const k = normalizeModuleKey(key);
    return (k === 'bill' || k === 'billing') && !!actions?.view;
  });
}

export function normalizeModuleKey(moduleName: string): string {
  const key = moduleName.trim().toLowerCase();

  if (MODULE_ALIASES[key]) return MODULE_ALIASES[key];
  if (key === 'labels') return 'label';
  if (key === 'account') return 'company';
  if (key === 'role') return 'roles';
  if (key === 'notifications') return 'notification';
  if (key === 'sla_policies') return 'sla_policy';
  if (key === 'integration') return 'integrations';

  return key;
}

export function normalizeModuleKeys(moduleName: string | string[]): string[] {
  return (Array.isArray(moduleName) ? moduleName : [moduleName]).map(normalizeModuleKey);
}

function hasTenantInLogin(loginApiData: unknown): boolean {
  if (!loginApiData || typeof loginApiData !== 'object') return false;
  if (!('tenant' in loginApiData)) return false;

  const tenant = (loginApiData as any).tenant;
  return tenant && typeof tenant === 'object';
}

export function getTenantName(loginApiData: unknown): string | null {
  if (!hasTenantInLogin(loginApiData)) return null;

  const tenant = (loginApiData as any).tenant;
  if (!tenant || typeof tenant !== 'object') return null;
  if (!('name' in tenant)) return null;

  const name = (tenant as any).name;
  return typeof name === 'string' && name.trim() ? name : null;
}

export function getTenantId(loginApiData: unknown): string | number | null {
  if (!hasTenantInLogin(loginApiData)) return null;

  const tenant = (loginApiData as any).tenant;
  if (!tenant || typeof tenant !== 'object') return null;

  if (!('id' in tenant)) return null;

  const id = (tenant as any).id;
  if (typeof id === 'string' && id.trim()) return id;
  if (typeof id === 'number') return id;
  return null;
}

export function getUserRole(loginApiData: unknown): string | null {
  if (!loginApiData || typeof loginApiData !== 'object') return null;
  if (!('role' in loginApiData)) return null;
  const role = (loginApiData as any).role;
  return typeof role === 'string' && role.trim() ? role : null;
}

/**
 * Transforms an array of permission strings (e.g., ["ticket.view", "contact.create"])
 * into a nested PermissionsMap (e.g., { ticket: { view: true }, contact: { create: true } })
 */
export function transformEffectivePermissionsToMap(permissions: string[]): PermissionsMap {
  const map: PermissionsMap = {};

  permissions.forEach((perm) => {
    if (typeof perm !== 'string') return;
    
    const parts = perm.split('.');
    if (parts.length === 2) {
      const module = normalizeModuleKey(parts[0]);
      const action = parts[1] as PermissionActionKey;

      if (!map[module]) {
        map[module] = {};
      }
      map[module][action] = true;
    }
  });

  return map;
}
